import React, { useEffect, useState } from 'react';
import { Modal, App, Typography } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setAvatarIcon } from '../../store/slices/authSlice';
import { useUpdateMyAvatarMutation } from '../../services/usersApi';
import AvatarIconPicker from '../common/AvatarIconPicker';
import UserAvatar, { getUserInitial } from '../common/UserAvatar';
import { sanitizeAvatarIcon } from '../../constants/avatarIcons';
import './profileModal.css';

const { Text } = Typography;

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Mi perfil": por ahora sólo el avatar, que es lo único que una persona puede
 * cambiarse a sí misma. Nombre, correo y rol se muestran de solo lectura porque
 * los administra la gerencia desde Usuarios — mostrarlos editables aquí sería
 * ofrecer algo que el backend rechaza por rol.
 */
const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose }) => {
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [updateMyAvatar, { isLoading }] = useUpdateMyAvatarMutation();

  // Saneado al cargar: una clave que este catálogo ya no dibuja se trata como
  // "sin icono", así no se reenvía al guardar (el backend la rechazaría).
  const [selected, setSelected] = useState<string | null>(
    sanitizeAvatarIcon(user?.avatarIcon)
  );

  // Re-sincroniza al abrir: si se cerró sin guardar, la próxima vez tiene que
  // volver a mostrar lo que está en la cuenta, no el descarte anterior.
  useEffect(() => {
    if (open) {
      setSelected(sanitizeAvatarIcon(user?.avatarIcon));
    }
  }, [open, user?.avatarIcon]);

  const initial = getUserInitial(user?.firstName, user?.email);

  const handleSave = async () => {
    try {
      const result = await updateMyAvatar(selected).unwrap();
      // El servidor devuelve lo que realmente guardó (normaliza vacío a null);
      // se refleja eso y no lo que se eligió aquí.
      dispatch(setAvatarIcon(result.avatarIcon));
      message.success('Avatar actualizado');
      onClose();
    } catch {
      message.error('No se pudo guardar el avatar');
    }
  };

  return (
    <Modal
      title="Mi perfil"
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={isLoading}
      width={460}
      destroyOnClose
    >
      <div className="profile-modal__identity">
        <UserAvatar
          size={56}
          firstName={user?.firstName}
          email={user?.email}
          avatarIcon={selected}
        />
        <div className="profile-modal__who">
          <div className="profile-modal__name">
            {user?.firstName} {user?.lastName}
          </div>
          <Text type="secondary" className="profile-modal__meta">
            {user?.role}
          </Text>
          <Text type="secondary" className="profile-modal__meta">
            {user?.email}
          </Text>
        </div>
      </div>

      <div className="profile-modal__section-label">Icono</div>
      <AvatarIconPicker
        value={selected}
        onChange={setSelected}
        initial={initial}
        disabled={isLoading}
      />
      <Text type="secondary" className="profile-modal__hint">
        Sirve para que te distingan de un vistazo cuando compartes dispositivo.
        Sin icono se usa tu inicial.
      </Text>
    </Modal>
  );
};

export default ProfileModal;
